import { LinearProgress } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { queryClient } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { Form, alert, RichEditorController, NewConfirmDialog } from 'components';
import { useCallbackPrompt, useWindowSize } from 'hooks';
import { richEditorStyles } from 'styles';
import { removeExtraSpaces } from 'utils';

import { commentFieldRules, COMMENT_MAX_LENGTH } from './constants';
import { ButtonsRow, CancelBtn, SubmitBtn } from './styled';
import { formStyles, loaderStyles } from './styles';

interface AddCommentFormProps {
  postId: string;
  isPostComment?: boolean;
}

export function AddCommentForm({ postId, isPostComment }: AddCommentFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const { mutateAsync: addNewCommentMutation, isLoading } = useMutation(
    API_HANDLERS.COMMENTS.CREATE,
    {
      onSuccess: () => {
        alert.success(t('comments.alerts.created'));
        queryClient.invalidateQueries(['comments-list']);
      },
    },
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitted },
  } = useForm<{ content: string }>({
    defaultValues: {
      content: '',
    },
  });

  const [isResetField, setResetField] = useState(false);

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(
    isDirty && !isSubmitted,
  );

  const handleReset = () => {
    setResetField((prev) => !prev);
    reset();
  };

  const handleFormSubmit = handleSubmit(async ({ content }) => {
    if (content.trim() === '') {
      return;
    }
    const formValue = removeExtraSpaces(content);
    await addNewCommentMutation({ content: formValue, postId });

    handleReset();
  });

  return (
    <div>
      <Form sx={formStyles} onSubmit={handleFormSubmit}>
        {isLoading && <LinearProgress color="info" sx={loaderStyles} />}

        <RichEditorController
          name="content"
          control={control}
          placeholder={t('comments.post.placeholder')}
          maxLength={COMMENT_MAX_LENGTH}
          rules={commentFieldRules}
          sx={richEditorStyles(isMobile)}
          isResetField={isResetField}
          withRightPadding
        />

        <ButtonsRow isPostComment={isPostComment}>
          <CancelBtn color="inherit" variant="outlined" onClick={handleReset}>
            {t('comments.cancelBtn')}
          </CancelBtn>
          <SubmitBtn color="info" variant="contained" type="submit" disabled={isLoading}>
            {t('comments.postBtn')}
          </SubmitBtn>
        </ButtonsRow>
      </Form>

      <NewConfirmDialog
        title={t('comments.modal.title')}
        subTitle={t('comments.modal.exit')}
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
        submitLabel={t('comments.modal.successButton')}
        cancelLabel={t('comments.modal.cancelButton')}
      />
    </div>
  );
}
